import { useTranslation } from 'react-i18next';
import { Accordion } from '@/common/components/Accordion/Accordion';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { CostSummaryTable } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/ConfirmDetaisStep/CostSummaryTable/CostSummaryTable';
import { FamilyMembersTable } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/ConfirmDetaisStep/FamilyMembersTable/FamilyMembersTable';
import { useInsuranceContext } from '@/common/contexts/insurance/InsuranceContext';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useCreatePlanConfirmation } from '@/common/integrations/menhir/employee/insurance/insurance.hooks';
import {
  InsurancePaymentMethod,
  TMenhirInsurancePaymentMethod,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { formatDateISO } from '@/common/utils/date';
import { buildTranslatedFaqs } from '@/common/utils/i18n';
import { determineTaxIdTypeByMarket } from '@/common/utils/tax';
import { notify } from '@/common/utils/toast/toast';
import { useMarket } from '@/web/hooks/useMarket';
import { useLocalSearchParams } from 'expo-router';
import { getFaqsI18nKey } from './ConfirmDetailsStep.utils';

export const ConfirmDetailsStep = ({
  paymentMethod,
  companyPaysAll,
}: TConfirmDetailsStepProps) => {
  const { t } = useTranslation();
  const { simulation, resetSimulation } = useInsuranceContext();
  const { mutateAsync, isLoading } = useCreatePlanConfirmation();
  const { dismiss } = useFlowLayout();
  const { slug } = useMarket();

  const selectedPersons = simulation.persons.filter(
    (person) => person.selected
  );

  const params = useLocalSearchParams<{ policyId: string }>();

  const faqs = buildTranslatedFaqs(
    getFaqsI18nKey({ market: slug, companyPaysAll, paymentMethod })
  );

  const onConfirmDetails = async () => {
    try {
      await mutateAsync({
        policy_id: params.policyId as string,
        payment_method: companyPaysAll
          ? 'company'
          : (paymentMethod as InsurancePaymentMethod),
        persons: simulation.persons
          .filter((p) => p.selected)
          .map((person) => ({
            ...(!person.isUserAdded ? { id: person.id } : {}),
            full_name: person.fullName,
            short_name: person.shortName,
            tax_id: person.taxNumber && {
              type: determineTaxIdTypeByMarket(slug),
              value: person.taxNumber.value,
            },
            type: person.type,
            gender: person.gender,
            birth_date: formatDateISO(person.birthdate),
            selected: person.selected,
          })),
      });
      resetSimulation();
      dismiss();
    } catch (error) {
      notify({
        message: (
          <Translate
            i18nKey="dashboard.insurance_detail.add_family_members.confirm.error.message"
            components={{
              reach_out: (
                <TextAction bold onPress={() => AnalyticsService.openChat()} />
              ),
            }}
          />
        ),
        type: 'error',
      });
    }
  };

  return (
    <FlowLayout.Content
      primaryAction={{
        children: t('general.navigation.confirm'),
        onPress: onConfirmDetails,
        loading: isLoading,
      }}
    >
      <CostSummaryTable
        simulation={simulation}
        paymentMethod={paymentMethod}
        selectedPersons={selectedPersons}
      />
      <FamilyMembersTable selectedPersons={selectedPersons} />
      {faqs.length > 0 && (
        <Accordion.Root
          title={t(
            'dashboard.insurance.simulate_family.step.confirm_details.faqs.title'
          )}
        >
          {faqs.map((faq, index) => (
            <Accordion.Item
              key={index}
              question={faq.question}
              answer={faq.answer}
            />
          ))}
        </Accordion.Root>
      )}
    </FlowLayout.Content>
  );
};

export type TConfirmDetailsStepProps = {
  paymentMethod?: TMenhirInsurancePaymentMethod;
  companyPaysAll: boolean;
};
